/**
 * Session timeout — a clinical/enterprise HMIS needs idle users
 * automatically logged out (a workstation left unattended in a nursing
 * station is a real security/compliance concern), but an ACTIVE user
 * must never be logged out just because the access token's own
 * 15-minute `exp` passed while the idle timer hadn't fired yet — that
 * would silently kill a session someone is actively working in. Two
 * independent triggers: (1) real user inactivity for `idleTimeoutMs`
 * (default 30 minutes — raised from the original 15: pharmacy billing
 * counter staff routinely go several minutes at a stretch without
 * touching the screen, e.g. handling cash or a paper prescription slip
 * mid-queue, while still genuinely at their workstation; 15 minutes was
 * logging them out mid-shift), via `useIdleTimer` — this is the only thing
 * that actually ends an active session; (2) the access token nearing its
 * `exp`, checked on a fixed poll — this does not force-log-out by itself:
 * it renews the token silently (`POST /auth/refresh`) `REFRESH_LEAD_MS`
 * BEFORE it lapses, and calls `onTimeout()` only when the server has
 * explicitly rejected the refresh token itself. A refresh that merely
 * could not be completed (rate limit, deploy restart, network blip) is
 * retried on the next poll and never ends a session. A
 * warning fires `warningBeforeMs` before the idle timeout so the user
 * has a real chance to stay logged in.
 */
import { useEffect, useState, useCallback } from 'react';
import { useIdleTimer } from './useIdleTimer';
import { useAuthStore } from '@store/auth.store';
import { attemptSilentRefresh } from '@core/api/http-client';

const TOKEN_EXPIRY_POLL_MS = 15_000;

/**
 * Renew this far BEFORE the access token's own `exp`, instead of waiting for it
 * to lapse.
 *
 * Refreshing only once the token was already dead left a window in which every
 * request the user's own typing generated failed with 401 and had to be rescued
 * by the interceptor's reactive refresh — so the fragile path ran constantly
 * instead of never, and a single bad moment inside that window (a rate limit, a
 * deploy, a dropped packet) surfaced to the user as a logout. Three minutes of
 * lead on a fifteen-minute token means the renewal happens quietly, with twelve
 * further poll cycles left to retry before anything the user does can 401.
 */
const REFRESH_LEAD_MS = 3 * 60_000;

/** The same signals `useIdleTimer` treats as activity, so the two cannot disagree. */
const WARNING_ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel'] as const;

export interface UseSessionTimeoutOptions {
  idleTimeoutMs?: number;
  warningBeforeMs?: number;
  onTimeout: () => void;
}

export function useSessionTimeout({
  idleTimeoutMs = 30 * 60_000,
  warningBeforeMs = 60_000,
  onTimeout,
}: UseSessionTimeoutOptions): { warningVisible: boolean; dismissWarning: () => void } {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isTokenExpired = useAuthStore((state) => state.isTokenExpired);
  const isTokenExpiringWithin = useAuthStore((state) => state.isTokenExpiringWithin);
  const [warningVisible, setWarningVisible] = useState(false);

  const { reset } = useIdleTimer({
    timeoutMs: idleTimeoutMs,
    enabled: isAuthenticated,
    onIdle: onTimeout,
  });

  /**
   * The warning must track REAL IDLENESS, not the wall clock since login.
   *
   * This timer was started once and never reset on activity, so "you will be
   * logged out in a minute" appeared 29 minutes after login to someone who had
   * been typing the whole time — while the idle timer beside it, which does
   * reset, was nowhere near firing. The warning was therefore not merely early
   * but WRONG, and dismissing it reset the idle timer for a user who had never
   * been idle.
   *
   * It now shares the idle timer's own activity events, so the two agree about
   * what idle means.
   */
  useEffect(() => {
    if (!isAuthenticated) return;
    const warnAfter = idleTimeoutMs - warningBeforeMs;
    let warnTimer: ReturnType<typeof setTimeout>;

    const restart = (): void => {
      clearTimeout(warnTimer);
      setWarningVisible(false);
      warnTimer = setTimeout(() => setWarningVisible(true), warnAfter);
    };

    restart();
    for (const eventName of WARNING_ACTIVITY_EVENTS) {
      window.addEventListener(eventName, restart, { passive: true });
    }
    return () => {
      clearTimeout(warnTimer);
      for (const eventName of WARNING_ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, restart);
      }
    };
  }, [isAuthenticated, idleTimeoutMs, warningBeforeMs]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const tick = (): void => {
      if (!isTokenExpiringWithin(REFRESH_LEAD_MS)) return;
      void attemptSilentRefresh().then((outcome) => {
        if (outcome.status === 'refreshed') return;

        /**
         * ONE FAILED REFRESH IS NOT A DEAD SESSION.
         *
         * This used to log out immediately, which made an active user's
         * session hostage to a single request: a network blip, a pm2 reload
         * mid-deploy, the rate limiter, or another tab winning the token-
         * rotation race were all indistinguishable from "your refresh token is
         * genuinely expired".
         *
         * 'unavailable' means the question was never answered — the next poll
         * simply asks again, and the user keeps working. Only an explicit
         * rejection is evidence of an ended session, and even that is re-checked
         * against expiry: `attemptSilentRefresh` adopts a token another tab
         * rotated into place, and any concurrent refresh writes to the same
         * store, so a token that is no longer expired means this attempt merely
         * lost a race. Only a session STILL expired after all that has ended.
         */
        if (outcome.status === 'unavailable') return;
        if (!isTokenExpired()) return;
        onTimeout();
      });
    };

    // Run once on mount too: after a reload the persisted access token is
    // routinely already past its `exp` (the session survives on the refresh
    // token), and waiting a full poll interval to notice would let the first
    // requests of the new page load fail for no reason.
    tick();
    const poll = setInterval(tick, TOKEN_EXPIRY_POLL_MS);
    return () => clearInterval(poll);
  }, [isAuthenticated, isTokenExpired, isTokenExpiringWithin, onTimeout]);

  const dismissWarning = useCallback(() => {
    setWarningVisible(false);
    reset();
  }, [reset]);

  return { warningVisible, dismissWarning };
}
